import { FishingMinigame } from '@phaser/minigames/fishing';
import { Fish } from '@phaser/minigames/fishing/Fish';
import { DEPTH } from '@phaser/data/depth';
import { Game } from '@phaser/scenes/Game';

export const BAR_WIDTH = 132;
export const BAR_HEIGHT = 16;

export class Indicator {
  private _scene = Game.current;
  private _fishing: FishingMinigame;

  private _tween: Phaser.Tweens.Tween;
  private _container: Phaser.GameObjects.Container;
  private _fish: Phaser.GameObjects.Image;
  private _rodPos: Phaser.GameObjects.Rectangle;
  private _progressBar: Phaser.GameObjects.Rectangle;

  private _id: string;
  private _isPointerDown = false;
  private _progress = 0.33;
  private _rodWidth  = 32;
  private _strength = 100;

  constructor(
    fishing: FishingMinigame,
    fish: Fish,
  ) {
    this._id = fish.id;
    this._fishing = fishing;
    this._strength = fish.strength;
    this._rodWidth = 28 * this._fishing.difficulty;

    this._placeIndicator(fish.x, fish.y);
    this._placeFish();
  }

  public update(_time: number, delta: number) {
    const rodSpeed = delta / 10;

    if (this._isPointerDown) {
      this._rodPos.setX(Math.min(BAR_WIDTH/2 - this._rodWidth/2, this._rodPos.x + rodSpeed));
    } else {
      this._rodPos.setX(Math.max(-BAR_WIDTH/2 + this._rodWidth/2, this._rodPos.x - rodSpeed));
    }

    const distance = Math.abs(this._rodPos.x - this._fish.x);
    if (distance < this._rodWidth / 2) {
      this._progress = Math.min(1, this._progress + delta / 5_000 * this._fishing.difficulty);
    } else {
      this._progress = Math.max(0, this._progress - delta / 7_500 / this._fishing.difficulty);
    }

    if (this._progress <= 0) {
      this.destroy();
      return this._fishing.stopFishing(this._id);
    }

    if (this._progress >= 1) {
      return this._fishing.collectFish();
    }

    this._progressBar.width = BAR_WIDTH * this._progress;
    const color = this._progress > 0.66 ? 0x00FF00 : this._progress > 0.33 ? 0xFFFF00 : 0xFF0000;
    this._progressBar.setFillStyle(color);
  }

  public destroy() {
    this._container.destroy();
    this._tween.stop();
  }
  
  public onPointerDown() {
    this._isPointerDown = true;
  }

  public onPointerUp() {
    this._isPointerDown = false;
  }

  private _placeIndicator(x: number, y: number) {
    this._container = this._scene.add.container(x, y - 64)
      .setDepth(DEPTH.MINIGAME + 1);

    const white = this._scene.add.rectangle(0, 0, BAR_WIDTH, BAR_HEIGHT, 0xFFFFFF, 0.2);
    this._rodPos = this._scene.add.rectangle(0, 0, this._rodWidth, BAR_HEIGHT, 0x00C0A7);
    this._progressBar = this._scene.add
      .rectangle(-BAR_WIDTH / 2, -14, BAR_WIDTH * this._progress, 4, 0xFFFFFF)
      .setOrigin(0, 0.5);

    this._container.add([white, this._rodPos, this._progressBar]);
  }

  private _placeFish() {
    this._fish = this._scene.add.image(0, 0, 'sprites', 'fish')
      .setScale(BAR_HEIGHT / 164);
    this._container.add([this._fish]);

    this._newFishMove();
  }

  private _newFishMove(): void {
    const maxX = BAR_WIDTH / 2 - this._fish.displayWidth / 2;
    const minX = -BAR_WIDTH / 2 + this._fish.displayWidth / 2;
    const newX = Math.floor(Math.random() * (maxX - minX + 1)) + minX;
    const distance = Math.abs(newX - this._fish.x);

    if (distance < 10) return this._newFishMove();

    this._tween = this._scene.tweens.add({
      targets: this._fish,
      x: newX,
      ease: 'Sine.easeInOut',
      duration: distance * 150 / this._strength,
      delay: 2000 / this._strength,
      onComplete: () => {
        this._newFishMove();
      },
    });
  }
}